import { supabase } from "../supabase";

export const ORG_ASSETS_BUCKET = "org-assets";
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const ALLOWED_MIME_TYPES = Object.freeze({
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
});

/**
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateOrganizationLogoFile(file) {
  if (!file) {
    return { valid: false, error: "No file selected." };
  }

  if (!ALLOWED_MIME_TYPES[file.type]) {
    return {
      valid: false,
      error: "Logo must be a PNG, JPEG, or WebP image.",
    };
  }

  if (file.size > MAX_LOGO_BYTES) {
    return { valid: false, error: "Logo must be 2 MB or smaller." };
  }

  return { valid: true };
}

function extensionForMime(mimeType) {
  return ALLOWED_MIME_TYPES[mimeType] || "png";
}

/** Stable storage path reuses the same public URL; bust browser/CDN cache after upsert. */
export function withLogoUrlCacheBust(publicUrl, version = Date.now()) {
  if (!publicUrl || typeof publicUrl !== "string") {
    return publicUrl;
  }

  const separator = publicUrl.includes("?") ? "&" : "?";
  return `${publicUrl}${separator}v=${version}`;
}

/**
 * Upload organization logo to Supabase Storage and return its public URL.
 *
 * @param {{ organizationId: string, file: File, client?: import("@supabase/supabase-js").SupabaseClient }} params
 * @returns {Promise<string>}
 */
export async function uploadOrganizationLogo({
  organizationId,
  file,
  client = supabase,
}) {
  if (!organizationId) {
    throw new Error("Organization id is required.");
  }

  const validation = validateOrganizationLogoFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const ext = extensionForMime(file.type);
  const objectPath = `${organizationId}/logo.${ext}`;

  const { error: uploadError } = await client.storage
    .from(ORG_ASSETS_BUCKET)
    .upload(objectPath, file, {
      upsert: true,
      contentType: file.type,
      cacheControl: "3600",
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data } = client.storage
    .from(ORG_ASSETS_BUCKET)
    .getPublicUrl(objectPath);

  if (!data?.publicUrl) {
    throw new Error("Unable to resolve logo URL after upload.");
  }

  return withLogoUrlCacheBust(data.publicUrl);
}

/**
 * @param {{ organizationId: string, client?: import("@supabase/supabase-js").SupabaseClient }} params
 */
export async function removeOrganizationLogo({
  organizationId,
  client = supabase,
}) {
  if (!organizationId) {
    return;
  }

  const extensions = ["png", "jpg", "webp"];
  const paths = extensions.map((ext) => `${organizationId}/logo.${ext}`);

  const { error } = await client.storage.from(ORG_ASSETS_BUCKET).remove(paths);

  if (error) {
    console.warn("[orgAssetUpload] logo remove failed", error);
  }
}
